import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getRoomBillsList } from '@utilities/order';
import Loader from '@components/Loader';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import PrintRoomKOT from '@src/app/components/Print/PrintRoomKOT';
import PrintRoomPDF from '@src/app/components/Print/PrintRoomPDF';
import Select from '@components/Select';
import { hasPermission, getFormattedDate, getRoomLabelText, formatPrintAmount } from '@utilities/common';
import { ImagesGS, PaymentTypes } from '@constants/common';
import { updateRoomOrderPayment, linkFoodBillsList, updateCollData } from '@utilities/order';
import Button from '@mui/material/Button';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmModal from '@components/ConfirmModal';
import { generatePaymentQR } from '@src/utilities/generatePaymentQR';
import moment from 'moment';
import { Dialog, DialogContent, DialogActions, Tooltip, Chip } from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NoDataFound from '@components/NoDataFound';
import ImagePreviewModal from '@components/ImagePreviewModal';
import {
    selectGuestListRestaurant,
    selectRoomBills,
    selectRoomBillingUserPermissions,
    selectSelectedRestaurant,
    selectSelectedRestId,
    selectUserRoles,
} from './roomBillingSelectors';

const RANGE_FETCH_DEBOUNCE_MS = 400;

function useDebouncedDaysFilter(days: any) {
    const isRange =
        days &&
        typeof days === 'object' &&
        days.type === 'range' &&
        days.startDate &&
        days.endDate;

    const [debouncedDays, setDebouncedDays] = useState(days);

    useEffect(() => {
        if (!isRange) {
            setDebouncedDays(days);
            return;
        }
        const timer = window.setTimeout(() => setDebouncedDays(days), RANGE_FETCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [days, isRange]);

    return isRange ? debouncedDays : days;
}

const iconBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: '50%',
  flexShrink: 0,
} as const;

/** Green when bill is fully covered by advance; yellow with ₹ when balance remains (pending shows “Pending”, else “Due”). */
function RoomBillPaymentBadge({ item }: { item: any }) {
  const total = Number(item?.totalCost) || 0;
  const advance = Number(item?.advancePayment) || 0;
  const outstanding = Math.max(0, Math.round((total - advance) * 100) / 100);
  const isPendingMode = String(item?.selectedPayment || '').toLowerCase() === 'pending';

  if (outstanding <= 0) {
    return (
      <Chip
        label="Paid in full"
        color="success"
        size="small"
        sx={{ height: 22, fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
      />
    );
  }

  const label = isPendingMode ? `Pending ₹${outstanding}` : `Due ₹${outstanding}`;
  return (
    <Chip
      label={label}
      color="warning"
      size="small"
      sx={{ height: 22, fontWeight: 600, '& .MuiChip-label': { px: 1 } }}
    />
  );
}

function GuestList({ days, roomBills, refreshList: _refreshList, restaurant, onSelectOrder, onRemoveOrder, selectedRestaurant, selectedRestId, roles, userPermissions, isSuperAdmin }: any) {
  const dispatch = useDispatch();
  const fetchDays = useDebouncedDaysFilter(days);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [loadingFoodBills, setLoadingFoodBills] = useState<boolean | false>(false);
  const [openDialog, setOpenDialog] = useState<boolean | false>(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');
  const [selectedOrderSnapshot, setSelectedOrderSnapshot] = useState<any>(null);
  const [QRDialog, setQRDialog] = React.useState(false);
  const [QRImage, setQRImage] = React.useState('');
  const [foodBills, setFoodBills] = React.useState(roomBills);
  const [imagePreviewOpen, setImagePreviewOpen] = React.useState(false);
  const [previewImages, setPreviewImages] = React.useState<string[]>([]);
  const [previewIndex, setPreviewIndex] = React.useState(0);
  const canReadTestBilling = hasPermission(userPermissions, "testBilling", "read");
  const canUpdateOrder = (roles && selectedRestId && ['admin'].includes(roles[selectedRestId].role)) || hasPermission(userPermissions, "updateOrder", "write");

  const params = useParams();

  const filteredEntries = useMemo(() => {
    if (!foodBills) {
      return [];
    }
    return Object.entries(foodBills).filter(([_key, item]: any) => {
      if (canReadTestBilling) {
        return true;
      }
      return item?.isGstBill;
    });
  }, [foodBills, canReadTestBilling]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch stable
  useEffect(() => {
    getRoomBillsList('admin', params?.restaurantId, dispatch, fetchDays);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch stable
  }, [params?.restaurantId, fetchDays]);

  useEffect(() => {
    setFoodBills(roomBills);
    setExpanded(false);
  }, [roomBills]);

  const getFoodBillForRooms = async (item: any, key: any) => {
    // console.log('food bills', item);
    setLoadingFoodBills(true);
    let roomNumArr: string[] = [];
    item?.rooms?.forEach((room: any) => {
      roomNumArr.push(room?.roomNumber);
    })
    // console.log('food bill', roomNumArr);
    let dataFoodBills: any = await linkFoodBillsList(item?.orderId, params?.restaurantId || '', roomNumArr, item?.checkInDate, item?.checkOutDate || moment().utc().valueOf(), !!item?.isGstBill);
    if(dataFoodBills?.length){
      setFoodBills({ ...(foodBills), [key]: { ...(foodBills)[key], foodBills: dataFoodBills } });
    }
    setLoadingFoodBills(false);
  };

  const handleSelectBlur = (event: any) => {
    event.stopPropagation();
  };
  const handleRemoveRoomBill = (event: any, orderId: any) => {
    event.stopPropagation();
    const bill = (roomBills || []).find((b: any) => b?.orderId === orderId) || { orderId };
    setSelectedOrderId(orderId);
    setSelectedOrderSnapshot(bill);
    setOpenDialog(true);
  };
  const handleDialogClose = (isConfirmed: boolean) => {
    if (isConfirmed) {
      onRemoveOrder(selectedOrderSnapshot || selectedOrderId);
      setExpanded(false);
    } else {
      console.log('not removed room bill', selectedOrderId);
    }
    setOpenDialog(false);
    setSelectedOrderId('');
    setSelectedOrderSnapshot(null);
  };

  const handleEditRoomBill = (event: any, order: any) => {
    event.stopPropagation();
    onSelectOrder(order);
  };

  const handleOpenImagePreview = (images: string[], startIndex: number = 0) => {
    setPreviewImages(images);
    setPreviewIndex(startIndex);
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImages([]);
    setPreviewIndex(0);
  };

  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };
  // const roomCostCalculated = (item: any) => {
  //   let itemsCost: number = 0;
  //   let gstCost: number =  0;
  //   let serviceCost: number =  0;
  //   let discount: number =  0;
  //   let totalCost: number =  0;
  //   item?.rooms?.forEach((room: any) => {
  //     itemsCost += Number(room?.roomCustomCost) * Number(room?.noOfNights);
  //   });
  //   // let gstCost = itemsCost * item?.restaurant?.roomGST * 0.01;
  //   // let serviceCost: number = itemsCost * item?.restaurant?.roomService * 0.01;
  //   // let discount: number = +(item?.discount);
  //   // let totalCost: number = +(itemsCost + gstCost + serviceCost - discount).toFixed(2);
  //   return {
  //     itemsCost,
  //     gstCost,
  //     serviceCost,
  //     discount,
  //     totalCost,
  //   };
  // }

  const handleLinkOff = (dataFoodBills: any, foodKey: any, roomKey: any) => {
    if(!!dataFoodBills?.length){

      dataFoodBills = dataFoodBills.filter((food: any, fKey: any) => fKey !== foodKey);
      // console.log('---', foodBills, dataFoodBills, roomKey, foodKey);
      // setFoodBills((prevFood: any) => (
      //   { ...prevFood, [roomKey]: { ...prevFood[roomKey], foodBills: [...dataFoodBills] } }
      // ));
      (foodBills) && setFoodBills({ ...(foodBills), [roomKey]: { ...(foodBills)[roomKey], foodBills: [...dataFoodBills] } });
      if(!!dataFoodBills?.length){
        updateCollData("room-bills", roomBills[roomKey]?.orderId, "foodBills", [...dataFoodBills]);
      }
    }
  }
  if((foodBills) === undefined) {
    return <Loader />;
  }
  if(foodBills?.length === 0){
    return <NoDataFound />;
  }
  return (
    <div className='orderMobWrap'>
      {
        filteredEntries.map(([key, item]: any) => (
            <Accordion
              className='orderCard'
              key={item.orderId}
              expanded={expanded === key}
              onChange={handleChange(key)}
              TransitionProps={{ unmountOnExit: true }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1bh-content"
                id="panel1bh-header"
                className="orderListWrap admin"
              >
                <Grid container spacing={2}>
                  <Grid item md={1} sm={2} xs={4}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.875,
                        width: '100%',
                        maxWidth: 92,
                        mx: 'auto',
                      }}
                    >
                      {!!item?.checkOutDate && (
                        <Tooltip title="Checked out" arrow placement="top" enterTouchDelay={0}>
                          <Box
                            aria-label="Checked out"
                            sx={{
                              ...iconBadgeSx,
                              width: 30,
                              height: 30,
                              bgcolor: 'success.main',
                              color: 'success.contrastText',
                              boxShadow: (theme: any) =>
                                `0 2px 10px ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.45)' : 'rgba(46, 125, 50, 0.35)'}`,
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Tooltip>
                      )}
                      <Box
                        component="img"
                        src={item?.isGstBill ? ImagesGS?.RoomIcon : ImagesGS?.NonRoomIcon}
                        alt=""
                        sx={{
                          width: 65,
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: 2,
                          display: 'block',
                          boxShadow: '0 4px 18px rgba(0, 0, 0, 0.12)',
                          border: '2px solid',
                          borderColor: item?.checkOutDate ? 'success.light' : 'divider',
                          bgcolor: 'background.paper',
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid container item md={5} sm={6} xs={8}>
                    <Grid item xs={12}>
                      <div className='customRowTable'>
                        <span>Order#: </span>
                        {item?.orderSeqNo || item?.uoId || item?.orderId}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div className='customRowTable' style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px' }}>
                        <span>Guest Name: </span>
                        {item?.guestName}
                        <RoomBillPaymentBadge item={item} />
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div className='customRowTable'>
                        <span>Check-In Date: </span>
                        {getFormattedDate(item?.checkInDate)}
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <div className='customRowTable'>
                        <span>Check-Out Date: </span>
                        {getFormattedDate(item?.checkOutDate)}
                        {!item?.checkOutDate && <Typography variant='body2' sx={{ bgcolor: 'var(--secondary)', color: '#fff', padding: '0 8px', display: 'inline-block', borderRadius: '4px' }}>In Progress</Typography>}
                      </div>
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                          <div className='customRowTable'>
                              <span>Payment: </span>
                              {item?.selectedPayment}
                          </div>
                      </Grid> */}
                    <Grid item xs={12}>
                      <div className='tags'>
                        <span>Created At: </span>
                        {getFormattedDate(item?.createdAt)}
                      </div>
                      <div className='tags'>
                        <span>Updated At: </span>
                        {getFormattedDate(item?.updatedAt)}
                      </div>
                    </Grid>
                  </Grid>
                  <Grid container item spacing={1} md={6} sm={4} xs={12} alignContent='center'>
                    <Grid item md={6} sm={12} xs={6} onClick={handleSelectBlur}>
                      {<Select
                        key={item?.orderId + item?.selectedPayment}
                        className="selectWrap"
                        defaultValue={{ label: item?.selectedPayment, value: item?.selectedPayment }}
                        options={PaymentTypes}
                        onChange={(val: any) => updateRoomOrderPayment(item?.orderId, val?.value || '')}
                      />}
                    </Grid>
                    <Grid item md={6} sm={6} xs={6}>
                      <PrintRoomKOT item={item} />
                    </Grid>
                    <Grid item md={6} sm={6} xs={6}>
                      <PrintRoomPDF restaurant={restaurant} item={item} />
                    </Grid>
                    {selectedRestaurant?.qrInfo && (
                      <Grid item md={6} sm={12} xs={6}>
                        <Button
                          fullWidth
                          variant='outlined'
                          color='secondary'
                          className='deleteBtn'
                          onClick={async (e) => {
                            e.stopPropagation();
                            const qr = await generatePaymentQR(
                              selectedRestaurant?.qrInfo,
                              item?.totalCost,
                              item?.orderSeqNo || item?.orderId
                            );
                            setQRImage(qr);
                            setQRDialog(true);
                          }}
                        >
                          <QrCodeScannerIcon /> Pay via QR
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {/* <Typography sx={{ color: 'text.secondary' }}>I am an accordion</Typography> */}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container>
                  {/* <Grid item md={4} sm={6} xs={12}>
                  <div className='customRowTable'><span>Check-in Date:</span> {getFormattedDate(item?.checkInDate)}</div>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                  <div className='customRowTable'><span>Check-out Date:</span> {getFormattedDate(item?.checkOutDate)}</div>
                </Grid> */}
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Guest Mobile:</span> {item?.guestMobile}</div>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Guest Address:</span> {item?.guestAddress}</div>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Other Guests:</span> {item?.guestOther}</div>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Guest Extra Info:</span> {item?.guestExtraInfo}</div>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Guest Company Name:</span> {item?.guestCompanyName}</div>
                  </Grid>
                  <Grid item md={4} sm={6} xs={12}>
                    <div className='customRowTable'><span>Guest GST:</span> {item?.guestCompanyGST}</div>
                  </Grid>
                  {!!item?.identificationFiles?.length && (
                    <Grid item xs={12}>
                      <div className='customRowTable' style={{ alignItems: 'center' }}>
                        <span>ID Uploads:</span>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginLeft: '8px' }}>
                          {item.identificationFiles.map((url: string, imgIdx: number) => (
                            <img
                              key={url}
                              src={url}
                              alt={`ID ${imgIdx + 1}`}
                              onClick={() => handleOpenImagePreview(item.identificationFiles, imgIdx)}
                              style={{
                                width: '50px',
                                height: '50px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                border: '1px solid #ccc',
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    </Grid>
                  )}
                </Grid>
                <Box className='cartItemsWrap' sx={{ flexGrow: 1 }}>
                  {
                    item?.rooms?.map((room: any, key: any) => (
                      <Grid key={room?.roomType + key} className='cartItems' display='flex'>
                        <Grid item xs={5} sx={{ textTransform: "capitalize", pl: 1 }}>
                          {room?.roomType} ({getRoomLabelText(room?.roomNumber?.split("-")[0], parseInt(room?.roomNumber?.split("-")[1]))})
                        </Grid>
                        <Grid textAlign="right" item xs={4} sx={{pr: 0.5}}>
                          ₹{room?.roomCustomCost}
                        </Grid>
                        <Grid item xs={2}>
                          x {room?.noOfNights} =
                        </Grid>
                        <Grid textAlign="right" item xs={3}>
                          ₹{room?.roomCustomCost * room?.noOfNights}
                        </Grid>
                      </Grid>
                    ))
                  }
                </Box>
                {!!item?.itemsCost && <p className='alignRight'>Rooms Cost: ₹{item?.itemsCost}</p>}
                {!!item?.gstBill && <p className='alignRight'>GST ({formatPrintAmount(item?.gstRate ?? item?.restaurant?.roomGST)}%): ₹{formatPrintAmount(item?.gstBill)}</p>}
                {!!item?.serviceBill && <p className='alignRight'>Service ({formatPrintAmount(item?.restaurant?.roomService)}%): ₹{formatPrintAmount(item?.serviceBill)}</p>}
                {!!item?.discount && <p className='alignRight'>Discount: ₹{Number(item?.discount)}</p>}
                {!!item?.advancePayment && <p className='alignRight'>Advance Payment: ₹{Number(item?.advancePayment)}</p>}
                {!!item?.totalCost && <p className='alignRight'><b>Total Rooms Bill: ₹{Number(item?.totalCost)}</b></p>}
                {/* <PrintRoomKOT item={item} /> */}
                <Grid container spacing={2}>
                  {canUpdateOrder &&
                      <Grid item sm={4} xs={6}>
                        <Button fullWidth variant="outlined" color='secondary' className='deleteBtn' onClick={(e: any) => handleEditRoomBill(e, item)}>
                          <EditIcon /> Edit
                        </Button>
                      </Grid>
                    }
                    {isSuperAdmin &&
                      <Grid item sm={4} xs={6}>
                        <Button fullWidth variant="outlined" color='error' className='deleteBtn' onClick={(e: any) => handleRemoveRoomBill(e, item?.orderId)}>
                          <DeleteForeverIcon /> Delete
                        </Button>
                      </Grid>
                    }
                  <Grid item sm={4} xs={12} marginLeft='auto'>
                    <Button fullWidth variant="outlined" color='primary' className='deleteBtn' onClick={(_e: any) => getFoodBillForRooms(item, key)}>
                      <AddLinkIcon /> Add Food Bills
                    </Button>
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ padding: '30px 0 5px 15px' }}>
                  {loadingFoodBills && <div style={{ textAlign: 'center', width: '100%' }}><Loader small /></div>}
                    {!!item?.foodBills?.length && <Grid container item alignItems="center" className='linkedFoodBills'>
                      <Grid item xs={2} sx={{overflow: 'hidden'}}>
                        Order#
                      </Grid>
                      {/* <Grid item xs={2} textAlign="center">
                        Items Cost
                      </Grid>
                      <Grid item xs={2} textAlign="center">
                        GST + Service
                      </Grid> */}
                      <Grid item xs={3} textAlign="center">
                        Total Cost
                      </Grid>
                      <Grid item xs={4} sx={{overflow: 'hidden'}}>
                        Billing Date
                      </Grid>
                      <Grid item xs={3} textAlign="center">
                        Remove
                      </Grid>
                    </Grid>}
                    {
                      item?.foodBills?.map((food: any, foodKey: any) => (
                        <Grid key={food?.orderId} container item alignItems="center" className='linkedFoodBills'>
                          <Grid sx={{overflow: 'hidden', wordBreak: 'break-all'}} item xs={2} textAlign="left">
                            {food?.orderSeqNo || food?.uoId || food?.orderId}
                          </Grid>
                          {/* <Grid item xs={2} textAlign="center">
                            {food?.itemsCost}
                          </Grid>
                          <Grid item xs={2} textAlign="center">
                            {Number(food?.gstCost + food?.serviceCost)}
                          </Grid> */}
                          <Grid item xs={3} textAlign="center">
                            {food?.totalCost}
                          </Grid>
                          <Grid item xs={4} sx={{overflow: 'hidden'}}>
                            {food?.createdAt}
                          </Grid>
                          <Grid item xs={3}>
                            <Button fullWidth variant="text" color='error' sx={{margin: '0'}} onClick={()=> handleLinkOff(item?.foodBills, foodKey, key)}>
                                <LinkOffIcon />
                            </Button>
                          </Grid>
                        </Grid>
                      ))
                    }
                </Grid>
              </AccordionDetails>
            </Accordion>
        ))
      }
      <Dialog open={QRDialog} onClose={() => setQRDialog(false)}>
        {/* <DialogTitle>SCAN TO PAY</DialogTitle> */}
        <DialogContent>
          {selectedRestaurant?.qrInfo?.pn && <Typography variant="h6" sx={{ fontSize: "1.2rem" }} gutterBottom align='center'>
            {selectedRestaurant?.qrInfo?.pn}
          </Typography>}
          <Typography variant='subtitle2' align='center' sx={{ fontWeight: 'bold', color: 'var(--primary)', fontSize: '1rem' }}>Scan and Pay</Typography>
          {QRImage ? <img style={{ maxWidth: '100%' }} src={QRImage} alt="" /> : 'QR CODE GENERATION FAILED'}
          {selectedRestaurant?.qrInfo?.pa && <Typography variant="h6" sx={{ fontSize: "1.2rem" }} gutterBottom align='center'>
            {selectedRestaurant?.qrInfo?.pa}
          </Typography>}
          {ImagesGS?.UPIIcon && <img
            src={ImagesGS?.UPIIcon}
            alt="UPI Icon"
            style={{ width: '250px', margin: '0 auto', display: 'block' }}
          />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQRDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <ConfirmModal open={openDialog} onClose={handleDialogClose} />
      <ImagePreviewModal
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        images={previewImages}
        initialIndex={previewIndex}
      />
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  roomBills: selectRoomBills(state),
  restaurant: selectGuestListRestaurant(state),
  selectedRestaurant: selectSelectedRestaurant(state),
  selectedRestId: selectSelectedRestId(state),
  roles: selectUserRoles(state),
  userPermissions: selectRoomBillingUserPermissions(state),
  isSuperAdmin: !!(state.user?.user?.isSuperAdmin),
});

export default connect(mapStateToProps)(React.memo(GuestList));
